import React, {useEffect, useState} from 'react';
import {Button, Col, Container, Row} from 'reactstrap';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {useUserState} from 'contexts/user';
import {usePhaseState} from 'funnel-schedule/phase-context';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import Header from 'components/Elements/Header';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Video from 'components/Video';
import {WebinarNew} from 'components/Webinar';
import IconList, {IconListItem} from 'components/Elements/IconList';
import CheckoutButton from 'components/CheckoutButton';
import {SalesDisclaimer} from 'components/SalesCta';
import {TestimonialBubble} from 'components/Testimonial';
import {ModuleAccordionV1} from 'components/ModuleAccordion';
import {BonusAccordion} from 'components/BonusList';
import Guarantee from 'components/Guarantee';
import {FaqListV1} from 'components/FaqList';
import RegistrationModal from 'components/RegistrationModal';
import ExitModal from 'components/ComponentLibrary/ExitModal';
import Footer from 'components/Elements/Footer';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching HBC Masterclass';

export default function ScreeningIndex({
  replayOverride = false,
  salesOverride = false,
  bucketOverride = undefined,
  variant = 'default',
  version = 'screening',
  ...props
}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const {phase, bucket: webinarBucket, report: webinarReport} = webinarSchedule;
  const {bucket: userBucket, events = []} = useUserState();
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  const hasCompletedMasterclass = events.includes(COMPLETED_MASTERCLASS_EVENT);
  const beforeWebinar = phase === 'before' && !replayOverride;
  const afterWebinar = (phase === 'after' && !replayOverride) || (phase === 'replay' && !replayOverride);
  const delayReveal =
    (hasCompletedMasterclass && !replayOverride && phase != 'before') || afterWebinar || salesOverride;
  const segment = webinarBucket ?? userBucket ?? '1';
  const workbookDownloadUrl =
    'https://cdn.foodrevolution.org/hbc/Brain%20Breakthroughs%20MASTERCLASS%20Private%20Workbook.pdf';

  const [upsellUrlControl, setupsellUrlControl] = useState(props.upsellUrlControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setupsellUrlControl('v1');
          break;
      }
    }, 1282);
  }, []);
  const checkoutUrl = {
    control: 'hbc-masterclass',
    v1: 'hbc-masterclass-uhc'
  }[upsellUrlControl];

  const productId = 'C-HBC';
  const productSku = 'C-HBC';
  const productName = 'Healthy Brain';
  const productSlug = checkoutUrl;
  const productValue = 247;
  const productPaymentPlanValue = 89;
  const productFullPrice = 497;
  const priceDiscount = 50;

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: productId,
        sku: productSku,
        name: productName,
        variant: productSlug,
        value: productValue,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_MASTERCLASS_EVENT)) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#join') {
        if (document.getElementById('watch-cta-thankyou')) {
          document.getElementById('watch-cta-thankyou').scrollIntoView();
        }
      }
    }
  }, [events, anchorScrolled]);

  function MasterclassLogo({className = null, logoColor = 'default', logoHeight = 90, imageClassName = undefined}) {
    return (
      <div className={classNames(`hbc-masterclass-logo`, className)}>
        <img
          src={
            {
              default: 'https://cdn.foodrevolution.org/hbc/hbc-bbm-icon-logo.svg',
              white: 'https://cdn.foodrevolution.org/hbc/hbc-bbm-icon-logo-white.svg',
              black: 'https://cdn.foodrevolution.org/hbc/hbc-bbm-icon-logo-black.svg'
            }[logoColor]
          }
          alt="break breakthroughs masterclass logo"
          style={{maxHeight: `${logoHeight}px`}}
          className={className}
        />
      </div>
    );
  }
  function CourseLogo({className = undefined, logoColor = 'default', logoHeight = 100, imageClassName = undefined}) {
    return (
      <div className={classNames(`hbc-course-logo`, className)}>
        <img
          src={
            {
              default: 'https://cdn.foodrevolution.org/hbc/hbc-logo.svg',
              white: 'https://cdn.foodrevolution.org/hbc/hbc-logo-white.svg',
              black: 'https://cdn.foodrevolution.org/hbc/hbc-logo-black.svg'
            }[logoColor]
          }
          alt="healthy brain logo"
          style={{maxHeight: `${logoHeight}px`}}
          className={imageClassName}
        />
      </div>
    );
  }
  function CourseImage({className = undefined}) {
    return (
      <div className={classNames(`hbc-course-image`, className)}>
        <img
          src="https://cdn.foodrevolution.org/hbc/hbc-product_image.png"
          width="1200"
          height="500"
          alt="Healthy Brain product graphic"
        />
      </div>
    );
  }
  function SalesCta({headline, btnText = 'Buy Now', className = undefined, noImage = false, trees = true}) {
    return (
      <div className={classNames(`cta-sales`, className)}>
        {headline && <div className="cta-headline text-center text-white">{headline}</div>}
        <div
          className={classNames(
            `offer-box`,
            `offer-box-shadow`,
            `px-3`,
            `py-4`,
            `px-sm-4`,
            `px-md-5`,
            `py-md-5`,
            `text-center`,
            `background-white`,
            `rounded`
          )}
        >
          {!noImage && (
            <Row className="justify-content-center mb-4">
              <Col xs="10" md="8" xl="7">
                <CourseImage />
              </Col>
            </Row>
          )}
          <h5 className="mt-0">
            Get instant access — even <br className="d-none d-md-block d-lg-none" />
            at 2 am – guaranteed!
          </h5>
          <h3 className="text-green mt-0 mb-2">
            All For Just: <del className="text-medium-gray">${productFullPrice}</del>
          </h3>
          <h2 className="text-green mt-0">Masterclass Special: ${productValue}</h2>
          <CheckoutButton className="offer-box-button" sku={productSku} price={productValue} slug={productSlug}>
            <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> {btnText}
          </CheckoutButton>
          <SalesDisclaimer />
          <p className="mt-4 mb-1">Prefer a 3-month payment plan? We’ve got you covered!</p>
          <CheckoutButton
            color="link"
            className="btn-link-checkout"
            sku={productSku}
            price={productPaymentPlanValue}
            slug={productSlug}
            queryParams={{opt: 1}}
          >
            Join now for just ${productPaymentPlanValue}/month!
          </CheckoutButton>
        </div>

        {trees && (
          <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
            <Col xs="11" lg="8" className="mb-4 mb-lg-0">
              <h6 className="section-only-heading text-center text-lg-left text-400">
                For every new order, we make a donation to Trees for the Future, enabling them to plant another organic
                fruit or nut tree in a low-income community.
              </h6>
            </Col>
            <Col xs="11" lg="3" className="text-center">
              <img
                alt="Trees for the Future logo"
                src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                width={1080}
                height={442}
                style={{maxHeight: '100px', width: 'auto'}}
              />
            </Col>
          </Row>
        )}
      </div>
    );
  }

  useBalanceText();
  useBeacon();
  return (
    <div className="page d-flex flex-column">
      <PageMeta title="Watch the Brain Breakthroughs Masterclass with Drs. Dean & Ayesha Sherzai" />

      <div className="page-content">
        <Header bgColor="black" lockupWhite />

        <SectionCL id="watch-header" color="white" type="triangle" angle="none" order="first">
          <Container>
            <h4 className="section-heading text-center mb-1">Brain Breakthroughs Masterclass</h4>
            <h5 className="mt-0 text-center">with Drs. Dean & Ayesha Sherzai Hosted by Ocean Robbins</h5>
            <h2 className="section-heading text-center">
              {
                {
                  1: <>10 Brain Health Breakthroughs That Can Slash Your Risk of Dementia</>,
                  2: <>10 Brain Health MUSTS for A Sharper Mind, Increased Productivity, and Maximum Focus</>
                }[segment]
              }
            </h2>
            {replayOverride ? (
              <>
                <Row className="d-flex justify-content-center">
                  <Col lg="10" className="video-col px-0 px-sm-3">
                    <Video
                      url={
                        'https://customer-ros8xhsmbkksflna.cloudflarestream.com/a72789d6d66a5ee0cb32a423c936f2b0/watch'
                      }
                      label="HBC - Masterclass"
                    />
                  </Col>
                </Row>
              </>
            ) : (
              <WebinarNew noun="Masterclass" label="HBC - Masterclass" />
            )}
            <h4 className="text-center text-600 mb-0">
              <a
                href="https://cdn.foodrevolution.org/hbc/Brain%20Breakthroughs%20MASTERCLASS%20Private%20Workbook.pdf"
                className="workbook-download text-underline"
                target="_blank"
              >
                Download your FREE workbook here.
              </a>
            </h4>
            <p className="balance-text text-center mt-4 m-0">
              IMPORTANT: Stay tuned ‘till the end for an inspiring story of hope from one of The Brain Docs’ clients
              that’s never been shared publicly before.
            </p>
          </Container>
        </SectionCL>

        {delayReveal && (
          <>
            <SectionCL
              id="watch-cta-thankyou"
              color="purple"
              innerClass="text-white text-center"
              angle="both"
              type="triangle"
            >
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <div className="background-green p-4 rounded mb-4 border-dashed border-white border-3">
                      <p className="m-0">
                        <b>One-time offer:</b> Special {priceDiscount}% off discount{' '}
                        <br className="d-none d-sm-block d-md-none" />
                        is only available on this page. Please do not <br className="d-none d-sm-block d-md-none" />
                        close or refresh this page.
                      </p>
                    </div>
                    <h3 className="mt-0">
                      You’re here because you care about your brain health. Ready to take the next step?
                    </h3>
                    <h2 className="mt-0">
                      Today only: enroll in the groundbreaking course, <i>Healthy Brain,</i> for {priceDiscount}% off
                    </h2>
                    <Row className="d-flex justify-content-center my-4">
                      <Col lg="9">
                        <img
                          src="https://cdn.foodrevolution.org/hbc/hbc-product_image-white.png"
                          width="1200"
                          height="500"
                          alt="Healthy Brain graphic"
                        />
                      </Col>
                    </Row>
                    <h4 className="section-heading">
                      Enroll Today for just ${productValue} + Get the Brain Health Collection for FREE!
                    </h4>
                    <CheckoutButton sku={productSku} price={productValue} slug={productSlug} className="mb-4">
                      Join Now!
                    </CheckoutButton>
                    {
                      {
                        1: (
                          <>
                            <h3 className="mt-4">The truth is Alzheimer’s doesn’t come out of nowhere.</h3>
                            <h3 className="mt-0 text-400">
                              In fact, the groundwork for dementia is laid years, even decades, before the onset of
                              symptoms.
                            </h3>
                            <h3 className="mt-0">
                              The time to take action is <i>now.</i>
                            </h3>
                          </>
                        ),
                        2: (
                          <>
                            <h3 className="mt-4">The truth is a sluggish brain doesn’t come out of nowhere.</h3>
                            <h3 className="mt-0 text-400">
                              Little things, like forgetting names and brain fog slowly set in. But left unchecked,
                              these annoyances will get worse.
                            </h3>
                            <h3 className="mt-0 text-400">
                              In fact, these seemingly harmless signs might be early red flags for dementia — which can
                              start decades before the onset of full blown symptoms.
                            </h3>
                            <h3 className="mt-0">
                              The time to take action is <i>now.</i>
                            </h3>
                          </>
                        )
                      }[segment]
                    }
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL
              id="watch-if-you-want"
              color="white"
              imgPosition="right"
              imgUrl={
                {
                  1: require('static/backgrounds/woman-headshot-square.jpg'),
                  2: require('static/backgrounds/woman-tablet-square.jpg')
                }[segment]
              }
              type="triangle"
            >
              <h2 className="section-heading">Imagine a future where you can:</h2>
              <IconList>
                {
                  {
                    1: (
                      <>
                        <IconListItem>
                          <b>Say no to depending</b> on caregivers to meet your basic needs
                        </IconListItem>
                        <IconListItem>
                          Replace fear of dementia and decreased brain function with{' '}
                          <b>positive and scientifically proven action</b>
                        </IconListItem>
                        <IconListItem>
                          Help to protect your family from the devastating financial burden that typically comes with a{' '}
                          <b>dementia diagnosis</b>
                        </IconListItem>
                        <IconListItem>
                          Say goodbye to the hype of fad diets and gimmicks — and{' '}
                          <b>discover what scientific studies</b> say about the{' '}
                          <b>best food for a long, dementia-free life</b>
                        </IconListItem>
                      </>
                    ),
                    2: (
                      <>
                        <IconListItem>
                          Say goodbye to the hype of fad diets and gimmicks — and{' '}
                          <b>discover what scientific studies</b> say about the foods that expand memory and mental
                          clarity
                        </IconListItem>
                      </>
                    )
                  }[segment]
                }
                <IconListItem>
                  <b>End brain fog</b> once and for all — even if you’ve been dealing with it for years
                </IconListItem>
                <IconListItem>
                  Prevent or <b>reverse “seemingly harmless” signs</b> of mental decline
                </IconListItem>
                <IconListItem>
                  <b>Improve your memory now,</b> and add up to 7.5 years of sharp cognition to your life
                </IconListItem>
                <IconListItem>
                  <b>Fall asleep faster, sleep deeper,</b> and wake up more refreshed than you ever thought possible
                </IconListItem>
                <IconListItem>
                  Delight your taste buds with <b>brain-boosting new recipes</b>
                </IconListItem>
                <IconListItem>
                  Best of all — discover how to finally <b>succeed at lasting habit change</b> by leveraging the latest
                  neuroscience
                </IconListItem>
              </IconList>
              <h5>
                <i>You're in the right place!</i>
              </h5>
            </SectionCL>

            <SectionCL
              id="join-tragic"
              color="light-gray"
              imgPosition="left"
              imgUrl={require('static/backgrounds/woman-puzzle.jpg')}
              type="triangle"
            >
              <h2 className="section-heading">
                Tragically, most people don’t give their brain the attention it deserves — until{' '}
                <span className="text-nowrap">it’s too late.</span>
              </h2>
              <h3 className="mt-0">Here are some scary facts:</h3>
              {
                {
                  1: (
                    <>
                      <p>
                        Most people over 85 will die with dementia.<sup>6,7</sup>
                      </p>
                      <p>
                        In a Kaiser study of 10,000 patients,{' '}
                        <b>
                          high blood cholesterol levels <i>during midlife</i>
                        </b>{' '}
                        predicted a 57% higher risk of developing Alzheimer's later on.<sup>8</sup>
                      </p>
                      <p>
                        A 1993 study showed that <b>people who ate certain foods</b> increased their risk of dementia by
                        50%.<sup>9</sup>
                      </p>
                      <p>
                        <b>Chronic stress</b> pumps adrenaline and cortisol into your body and causes brain
                        inflammation. Over time, this can damage your brain.
                      </p>
                    </>
                  ),
                  2: (
                    <>
                      <p>
                        <b>Chronic stress</b> pumps adrenaline and cortisol into your body and causes brain
                        inflammation. Over time, this can damage your brain.<sup>10</sup>
                      </p>
                      <p>
                        Most people over 85 will die with dementia.<sup>6,7</sup>
                      </p>
                      <p>
                        <b>
                          Many people just want to boost their productivity and might not be thinking about Alzheimer's
                          yet. But there is a startling link between how you treat{' '}
                          <br className="d-none d-lg-block d-xl-none" />
                          your brain now… and what the future <br className="d-none d-lg-block d-xl-none" />
                          may hold for you.
                        </b>
                      </p>
                    </>
                  )
                }[segment]
              }
            </SectionCL>

            <SectionCL
              id="join-deadly"
              color="white"
              imgPosition="right"
              imgUrl={require('static/backgrounds/brain-chart.jpg')}
              type="triangle"
            >
              <h3 className="section-heading">Is Alzheimer's more deadly than we thought?</h3>
              <p>
                <b>
                  Officially, Alzheimer’s disease is the <br className="d-none d-lg-block d-xl-none" />
                  sixth-leading cause of death in the <br className="d-none d-lg-block d-xl-none" />
                  United States.
                </b>
                <sup>1</sup>
              </p>
              <p>But many researchers believe that Alzheimer’s is grossly underreported on death certificates.</p>
              <p>
                In fact, it might be America’s third-most deadly disease, behind only heart disease and cancer.
                <sup>2</sup>
              </p>
              <p>
                While nearly every chronic disease you can think of — cardiovascular disease, cancer, stroke, HIV — is
                in decline, <b>deaths due to Alzheimer’s have increased by nearly 87% in the last decade.</b>
                <sup>3</sup>
              </p>
            </SectionCL>

            <SectionCL
              id="join-imagine"
              color="light-gray"
              imgPosition="left"
              imgUrl={
                {
                  1: require('static/backgrounds/playing-games.jpg'),
                  2: require('static/backgrounds/elderly-couple-dancing.jpg')
                }[segment]
              }
              type="triangle"
            >
              <h2 className="section-heading">
                {
                  {
                    1: <>Imagine being able to actually enjoy your golden years!</>,
                    2: <>Imagine being able to perform at your best, every single day!</>
                  }[segment]
                }
              </h2>

              <p>
                Many of us can reasonably expect to live beyond 85, especially if treatments for major diseases continue
                to improve. This means that almost every family will be affected by dementia.
              </p>
              <p>
                <b>The emotional cost of the disease is immeasurable.</b>
              </p>
              <p>
                <b>
                  <i>And the financial cost is absolutely overwhelming.</i>
                </b>{' '}
                Unprepared families can be devastated by this disease as they pay for their loved one’s — or even{' '}
                <i>multiple family members’</i> — care.
              </p>
              <p>
                In fact, Alzheimer’s is the 4th costliest disease in America. And rates of Alzheimer’s are expected to
                triple in the next generation!<sup>4,5</sup>
              </p>
              <h4 className="mt-4">Ready for the Good News?</h4>
              <h5 className="mt-0">
                The right diet and lifestyle choices can dramatically boost brain power, slash Alzheimer’s risk, and add
                7 ½ more years of healthy brain function to your life.
              </h5>
            </SectionCL>

            <SectionCL
              id="join-thought"
              color="white"
              imgPosition="right"
              imgUrl={require('static/backgrounds/woman-worried.jpg')}
              type="triangle"
            >
              <h3 className="section-heading">Have you ever thought...</h3>
              {
                {
                  1: (
                    <>
                      <p>
                        “It won’t happen to me. I’m still young and have my wits together. I’ll just deal with it later
                        if I need to.”
                      </p>
                      <p>“There’s not much I can do to prevent dementia — it’s in my family.”</p>
                      <p>
                        “Everyone forgets names and faces and why they walked into the kitchen once they hit 40. It’s
                        normal.”
                      </p>
                      <p>
                        “Am I going to have to take fistfuls of supplements every day to avoid what happened to my
                        parents or grandparents?”
                      </p>
                      <p>“I would hate to be so helpless and make my family look out for me all the time.”</p>
                      <p>“I’m scared of losing control.”</p>
                    </>
                  ),
                  2: (
                    <>
                      <p>“What happened to my productivity and energy? Is it just downhill from here?”</p>
                      <p>“Am I always going to be this drained and stressed out? What nutrients am I missing?”</p>
                      <p>“I can’t figure out how to get a good night’s sleep. I’ve tried everything.”</p>
                      <p>
                        “Why do I need to pay attention to brain health right now, especially since I have other health
                        concerns?”
                      </p>
                      <p>
                        “Everytime I try to be more healthy, I make progress for a few days but can’t sustain it.
                        Motivation comes easy to others. What am I missing? How can I retrain my brain?”
                      </p>
                    </>
                  )
                }[segment]
              }
            </SectionCL>

            <SectionCL id="" color="black" innerClass="text-white text-center" type="triangle">
              <Container>
                <Row className="d-flex justify-content-center mb-4">
                  <Col lg="11" xl="9">
                    {
                      {
                        1: (
                          <>
                            <p>
                              Whether you want to dramatically reduce your risk{' '}
                              <br className="d-none d-md-block d-lg-none" />
                              of Alzheimer’s and dementia, maintain your independence,{' '}
                              <br className="d-none d-md-block d-lg-none" />
                              or simply keep your mind vibrant at all ages, you’ll find{' '}
                              <br className="d-none d-md-block d-lg-none" />
                              evidence-based answers right here.
                            </p>
                            <p>
                              You’ll be amazed at what you uncover inside <br className="d-none d-sm-block d-md-none" />
                              <i>Healthy Brain</i> — and how quickly you can enjoy{' '}
                              <br className="d-none d-sm-block d-md-none" />
                              more clarity, memory, and productivity.
                            </p>
                          </>
                        ),
                        2: (
                          <>
                            <p>
                              Whether you want to boost your mental performance, <br className="d-none d-lg-block" />
                              have the best memory possible, or experience all-day productivity,{' '}
                              <br className="d-none d-lg-block" />
                              you’ll find evidence-based answers right here.
                            </p>
                            <p>
                              You’ll be amazed at what you uncover inside <i>Healthy Brain</i>{' '}
                              <br className="d-none d-lg-block" />— and how quickly you can go from feeling “okay” —{' '}
                              <br className="d-none d-lg-block" />
                              to enjoying outstanding energy and focus.
                            </p>
                          </>
                        )
                      }[segment]
                    }
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <CourseLogo logoColor="white" logoHeight={75} />
                    <h2 className="text-white text-center mt-4 mb-3">
                      “Your everyday choices, <i>not your genetics,</i> determine your cognitive fate.”
                    </h2>
                    <h4 className="text-center text-white m-0">— Drs. Dean & Ayesha Sherzai</h4>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="purple" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <SalesCta
                      headline={
                        <>
                          <h2 className="section-heading">Ready Now? Join Us!</h2>
                          <h3 className="mt-4">Unlimited Access. Enjoy it all as much as you want.</h3>
                        </>
                      }
                    />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="light-gray" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <p className="text-small text-center mb-5">
                      <i>
                        These testimonials reflect people’s unique experience with The Brain Docs’ work.{' '}
                        <br className="d-none d-md-block d-lg-none" />
                        Your specific experience will vary.
                      </i>
                    </p>
                    <TestimonialBubble id="neil-s-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="sharon-p-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="lynette-p-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="ruth-g-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="donna-h-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="steve-f-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="paul-m-v1" bgColor="white" className="mb-5" />
                    <TestimonialBubble id="mo-s-v1" bgColor="white" />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="white" type="triangle">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h3 className="section-heading text-center text-400">
                      Discover the astonishing brain health secrets so you can{' '}
                      <b>
                        <i>make the most of your life.</i>
                      </b>
                    </h3>
                    <IconList>
                      <IconListItem>6 modules, each with 7 video lessons</IconListItem>
                      <IconListItem>
                        All lessons available in HD video, MP3 audio, and downloadable transcript format
                      </IconListItem>
                      <IconListItem>Access to an Exclusive Forum (that’s NOT on Facebook)</IconListItem>
                      <IconListItem>
                        100% plant-based, non-GMO recipes that are super easy to implement — with gluten-free and
                        soy-free options
                      </IconListItem>
                      <IconListItem>
                        6 delightful recipe preparation videos with neuroscientist and plant-based chef Dr. Ayesha
                        Sherzai (see exactly how she does it!)
                      </IconListItem>
                      <IconListItem>
                        11 workbooks and bonus resources to help you track your progress and implement everything you
                        learn in the course.
                      </IconListItem>
                      <IconListItem>
                        Q&A with Dean and Ayesha Sherzai, MD, hosted by Ocean Robbins – where we answer the TOP
                        questions from our community (pre-recorded, transcribed, and super easy to navigate so you can
                        get the answers YOU want).
                      </IconListItem>
                    </IconList>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="light-gray" type="triangle">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h5 className="section-heading">6 modules, each with 7 video lessons</h5>
                    <p>
                      You pick how you learn best! Each lesson is available in HD video, MP3 audio, and downloadable
                      transcript format. Simply press play and watch the videos, download the audios to listen
                      on-the-go, or grab the transcript for a riveting morning read.
                    </p>
                    <h5 className="mt-4">Cooking demos and recipes to mend & maximize brain power</h5>
                    <p>
                      What’s the best way to learn something new and make sure you remember it? By watching someone show
                      you how! Join neuroscientist and plant-based chef Dr. Ayesha Sherzai as she shows you exactly how
                      to prepare brain-healthy meals like Portobello Mushroom Pizza and Cashew Parmesan — easily,
                      affordably, and scrumptiously.
                    </p>
                    <p>
                      If you’re like us, these 100% plant-based, non-GMO recipes will become your new weeknight
                      favorites (Gluten-free and soy-free? We’ve got you covered.)
                    </p>
                    <h5 className="mt-4">More than a course — it’s a community with ongoing support!</h5>
                    <p>
                      As soon as you join, you’ll be welcomed into our private online community forum, where you can
                      connect with peers, and share struggles and successes.
                    </p>
                    <p>
                      You’ll love being able to get support from our Team on your brain health questions. Come back any
                      time you have a new question or concern!
                    </p>
                    <p>(And by the way, this forum is NOT on Facebook.)</p>
                    <h5 className="mt-4">
                      11 workbooks and bonus resources to help you put everything you learn to use.
                    </h5>
                    <p>
                      Only in the last fifteen years have we mapped out brain regions and uncovered incredible insights
                      into how the brain really works. But knowledge without action won’t get results — which is why
                      everything in this course is brilliantly designed to help you USE what you learn.
                    </p>
                    <p>
                      (And yes, we employed the latest brain science to develop these workbooks, making them not just
                      full of useful tools... but conducive to the way your mind loves to learn.)
                    </p>
                    <h5 className="mt-4">Bonus Q&A with Dean and Ayesha Sherzai, MD, and Ocean Robbins</h5>
                    <p>
                      We answer the TOP questions from our community (pre-recorded and transcribed so you can get the
                      answers YOU want).
                    </p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading text-uppercase">
                      The <i>Healthy Brain</i> Foundation
                    </h3>
                    <p>
                      Built upon the revolutionary <b>NEURO Plan,</b> the 6 Modules in <i>Healthy Brain</i> cover the
                      essential steps to achieve the healthiest brain possible.
                    </p>
                    <p>
                      NEURO is a comprehensive and holistic plan that encompasses all the key areas for optimal brain
                      health: <b className="text-underline">N</b>utrition, <b className="text-underline">E</b>
                      xercise, <b className="text-underline">U</b>
                      nwind, <b className="text-underline">R</b>estorative sleep, and{' '}
                      <b className="text-underline">O</b>ptimize.
                    </p>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col>
                    <ModuleAccordionV1 />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h4>
                      <i>Healthy Brain</i> could be the most important course you ever take.
                    </h4>
                    <h4 className="mt-0">Uncover what the world’s top brain health experts want you to know…</h4>
                    <h4 className="m-0">
                      {
                        {
                          1: (
                            <>
                              So you can think more clearly and improve memory{' '}
                              <b>
                                <i>now</i>
                              </b>{' '}
                              — and take action to slash your risk of Alzheimer’s disease and other dementias.
                            </>
                          ),
                          2: (
                            <>
                              So you can sharpen your brain performance, increase your memory power, and be amazed by
                              your newfound productivity.
                            </>
                          )
                        }[segment]
                      }
                    </h4>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="watch-special" color="light-gray" type="triangle">
              <Container>
                <Row className="d-flex justify-content-center">
                  <Col lg="10">
                    <h2 className="section-heading">About Today's Special Early-Bird Enrollment Discount</h2>
                    <p>
                      This early-bird discount is only for Masterclass attendees — as a reward for investing an hour in
                      this mission-critical aspect of your health.
                    </p>
                    <p>
                      *You can <i>only</i> unlock your special price on this page.*
                    </p>
                    <p>
                      <b>
                        With this Special Masterclass Price, you save $250 by enrolling in{' '}
                        <br className="d-none d-xl-block" />
                        <i>Healthy Brain</i> today.
                      </b>
                    </p>
                    <p>
                      Order from this page to get the best offer possible. (Do not hit refresh or close the page!) The
                      enrollment price is higher everywhere else on our site.
                    </p>
                    <Row className="d-flex justify-content-center my-4">
                      <Col lg="9">
                        <img
                          src="https://cdn.foodrevolution.org/hbc/hbc-product_image.png"
                          width="1200"
                          height="500"
                          alt="Healthy Brain graphic"
                        />
                      </Col>
                    </Row>
                    <h3 className="mt-0">Why Order Today: 3 Powerful Advantages Only Available Now</h3>
                    <h4>1. The Lowest Price That Will Ever Be Offered.</h4>
                    <p>
                      The regular price for <i>Healthy Brain</i> is ${productFullPrice}.
                    </p>
                    <p>
                      For all that you’ll get — the course, the community, access to our Team so you can ask questions
                      and get answers, and bonus gifts worth over $300. That’s already a rock bottom price!
                    </p>
                    <p>But when you enroll today, you'll get everything for only ${productValue}.</p>
                    <p>Here's why we're offering this to you:</p>
                    <p>
                      <b>We’re on a mission to change the way the world understands brain health.</b>
                    </p>
                    <p>
                      When you watched the Masterclass just now, you dipped your toes into the fascinating — and often
                      tragically overlooked — topic of brain health.
                    </p>
                    <p>
                      This tells us you’re already one of the special people who know that this information matters, and
                      who’s willing to think and act differently.
                    </p>
                    <p>
                      In other words, you’re the exact kind of person we want in this program. We want to do everything
                      we can to help you go deeper, and put what you learn into <i>action.</i>
                    </p>
                    <p>
                      With your help, we can open the eyes of millions of people around the world. When you know what to
                      eat, what to do, how to sleep better, how to be your sharpest self, and how to help your family
                      and friends make better choices, you make the world a better place... just by being yourself!
                    </p>
                    <p>
                      <b>So we want to make it easy for you to say yes now.</b>
                    </p>
                    <h4>2. Get the Brain Health Collection as a totally free bonus. (Value $97)</h4>
                    <p>
                      When you sign up for Healthy Brain today, you’ll get the <b>FREE Brain Health Collection</b> for{' '}
                      <i>even</i> more brain health power…
                    </p>
                    <IconList>
                      <IconListItem>
                        Enjoy 6 recipes for maximum brain health, including Raw Chocolate Mousse Pie, Rainbow Smoothie,
                        and the Green Goddess Sandwich
                      </IconListItem>
                      <IconListItem>
                        <i>Brain Food: 8 Superfoods Your Brain Will Love</i> (downloadable guidebook).
                      </IconListItem>
                      <IconListItem>
                        4 more brilliant interviews with leading brain health experts including Dale Bredesen, MD,
                        Daniel Amen, MD, David Perlmutter, MD, Dean & Ayesha Sherzai, MDs. Find out about{' '}
                        <i>The End of Mental Illness, Breakthroughs You Need to Know for a Healthy Brain,</i> and{' '}
                        <i>Simple Strategies You Can Use to Prevent Alzheimer’s & Dementia.</i>
                      </IconListItem>
                      <IconListItem>And more.</IconListItem>
                    </IconList>
                    <p>
                      It’s a $97 value — yours <b>free</b> when you join <br className="d-none d-sm-block d-md-none" />
                      <i>Healthy Brain</i> today.
                    </p>
                    <h4>3. Join With Other Participants</h4>
                    <p>
                      The genius of <i>Healthy Brain</i> is in its learning design. It’s carefully crafted to build
                      momentum and success. And it’s more fun to go on the journey with peers! When you join in, you’ll
                      get access to our private and moderated online community forum where you can connect with peers,
                      share struggles and successes, and ask for expert advice. And by the way, this forum is NOT on
                      Facebook. It’s a custom forum that our team built specifically to support you.
                    </p>
                    <p>
                      When you register for <i>Healthy Brain</i>, you’ll become part of an inspiring community who cares
                      about living to their fullest potential.
                    </p>
                    <p>This is More Than a Course — It’s a Community!</p>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading">Enroll Today & Get Brain-Boosting Bonuses (Value $300+!):</h2>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <BonusAccordion />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <h4>Unlimited Access. Enjoy it all as much as you want.</h4>
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="purple" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <SalesCta
                      headline={
                        <>
                          <h2 className="section-heading mb-3">Special Offer</h2>
                          <p className="mb-4">Exclusively for Masterclass participants. From this page only.</p>
                        </>
                      }
                    />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="watch-guarantee" color="light-gray" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <Guarantee />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="watch-faq" color="white" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading">Frequently Asked Questions</h2>
                    <FaqListV1 />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="watch-cta-final" color="purple" type="triangle">
              <Container>
                <Row>
                  <Col>
                    <SalesCta headline={undefined} trees={false} />
                  </Col>
                </Row>
              </Container>
            </SectionCL>

            <SectionCL id="" color="light-gray" order="last" angle="none">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading">References</h3>
                    <ul className="list-references list-group text-small mb-0">
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[1]</div>{' '}
                        <div>
                          “Dementia”. World Health Organization. 19 Sept 2019. Available from:
                          https://www.who.int/news-room/fact-sheets/detail/dementia
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[2]</div>{' '}
                        <div>
                          “Alzheimer’s Disease Fact Sheet.” NIH. National Institute on Aging. Reviewed 22 May 2019.
                          Available from: https://www.nia.nih.gov/health/alzheimers-disease-fact-sheet
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[3]</div>{' '}
                        <div>
                          2020 Alzheimer's disease facts and figures [published online ahead of print, 2020 Mar 10].
                          Alzheimers Dement. 2020;10.1002/alz.12068. doi:10.1002/alz.12068
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[4]</div>{' '}
                        <div>
                          Alzheimer’s Association. 2019 Alzheimer’s Disease Facts and Figures. Alzheimers Dement.
                          2019;15(3):321-87. Available from:
                          https://www.alz.org/media/Documents/alzheimers-facts-and-figures-2019-r.pdf
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[5]</div>{' '}
                        <div>
                          “Dementia cases set to triple by 2050 but still largely ignored.” World Health Organization.
                          11 April 2012. Available from:
                          https://www.who.int/mediacentre/news/releases/2012/dementia_20120411/en/
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[6]</div>{' '}
                        <div>
                          “What Is Dementia? Symptoms, Types, and Diagnosis.” National Institute on Aging. Reviewed 31
                          Dec 2017. Available from:
                          https://www.nia.nih.gov/health/what-dementia-symptoms-types-and-diagnosis
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[7]</div>{' '}
                        <div>
                          “2019 Alzheimer’s Statistics.” Alzheimers.net. Available from:
                          https://www.alzheimers.net/resources/alzheimers-statistics
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[8]</div>{' '}
                        <div>
                          Solomon A, Kivipelto M, Wolozin B, Zhou J, Whitmer RA. Midlife serum cholesterol and increased
                          risk of Alzheimer's and vascular dementia three decades later. Dement Geriatr Cogn Disord.
                          2009;28(1):75-80. doi: 10.1159/000231980.
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[9]</div>{' '}
                        <div>
                          “New MIND Diet May Significantly Protect Against Alzheimer’s Disease.” RUSH.edu. Available
                          from:
                          https://www.rush.edu/news/new-mind-diet-may-significantly-protect-against-alzheimers-disease
                        </div>
                      </li>
                      <li className="list-group-item reference-item">
                        <div className="reference-number">[10]</div>{' '}
                        <div>
                          Ouanes S, Popp J. High Cortisol and the Risk of Dementia and Alzheimer's Disease: A Review of
                          the Literature. Front Aging Neurosci. 2019 Mar 1;11:43. doi: 10.3389/fnagi.2019.00043.
                        </div>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          </>
        )}
      </div>

      {replayOverride && !salesOverride ? (
        <SectionCL
          id="replay-cta"
          color="purple"
          className="text-center text-white"
          angle="top"
          type="triangle"
          innerClass="pt-5"
        >
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">
                  Are you interested in enrolling in Drs. Dean and Ayesha’s <i>Healthy Brain</i> course?
                </h4>
                <h5>Click below to learn more:</h5>
                <Button className="btn-join btn-lg" color="cta" href="/join/" target="_blank">
                  Join Now!
                </Button>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      ) : (
        <>
          {!salesOverride && <RegistrationModal />}
          <ExitModal
            exitDelay={30000}
            content={
              <>
                <h4 className="section-heading mb-4">Did you know?</h4>
                <p className="mb-0">
                  Most Alzheimer’s cases can be prevented with <br className="d-none d-sm-block d-md-none" />
                  these 10 mighty brain breakthroughs.
                </p>
              </>
            }
            image={{
              url: require('static/backgrounds/women-laughing.jpg'),
              alt: '',
              width: 1000,
              height: 800
            }}
            positiveButtonText={<>Keep watching and learn the TRUTH</>}
            negativeButtonText="No Thanks"
            scrollHref="watch-header"
            scrollOffset={0}
          />
        </>
      )}
      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
